import React, {Component} from 'react';
import {Marker, Polygon, Polyline} from 'react-google-maps';

// import projectsActions from '~redux/Projects/actions';
import {darkBlue, mountainMeadow, orange} from '~utils/colors';

import {Project} from '../../types';
import ProjectPopup from '../ProjectPopup';

import togeojson from "@mapbox/togeojson";


import {
    getMarkerImage,
    MARKER_HEIGHT,
    MARKER_OFFSET_HORIZONTAL,
    MARKER_OFFSET_VERTICAL,
    MARKER_WIDTH,
} from './constants';
import {Route} from "~services/RoutesService";
import JSZip from "jszip";

interface Props {
    project: Project | Route;
    hovered: boolean;
    visible: boolean;
    selectable: boolean;
    selected?: boolean;
    onSetSelected?: (event: React.MouseEvent) => void;
    onUnsetSelected?: (event: React.MouseEvent) => void;
    activeSettings?: {
        strokeColor?: string;
        strokeWeight?: number;
        fillColor?: string;
        fillOpacity?: number;
    },
    inactiveSettings?: {
        strokeColor?: string;
        strokeWeight?: number;
        fillColor?: string;
        fillOpacity?: number;
    }
}

interface State {
    polylines: any[];
    markers: any[];
    polygons: any[];
    multi_polygons: any[];
    selected: boolean;
}

class ProjectMarker extends Component<Props, State> {

    public props;
    public active = true;
    public imageName;
    public icon;
    public markerProps;

    constructor(props) {
        super(props);

        this.props = props;
        this.state = {
            polylines: [],
            markers: [],
            polygons: [],
            multi_polygons: [],
            selected: false
        }
        const {project} = this.props;
        const mapFile = project?.kml ? project.kml : project.kmz;
        if (mapFile) {
            const gjsonPromise = this.getGeoJSON(mapFile);
            this.setPolylines(gjsonPromise);
        }
    }


    getActiveSettings = () => {
        const {activeSettings} = this.props;
        const defaultActiveSettings = {
            strokeColor: orange,
            strokeWeight: 2,
            fillColor: orange,
            fillOpacity: 0.3
        }
        return {...defaultActiveSettings, ...activeSettings};
    }

    getInactiveSettings = () => {
        const {inactiveSettings} = this.props;
        const defaultInactiveSettings = {
            strokeColor: mountainMeadow,
            strokeWeight: 2,
            fillColor: mountainMeadow,
            fillOpacity: 0.3
        }
        return {...defaultInactiveSettings, ...inactiveSettings};
    }

    getGeoJSON = async (url) => {
        if (url.toLowerCase().endsWith('.kmz')) {
            const response = await fetch(url);
            const blob = await response.blob();
            const zip = new JSZip();
            const zipFile = await zip.loadAsync(blob);
            const kmlFile = Object.keys(zipFile.files).find(f => f.endsWith('.kml'));
            if (kmlFile === undefined) {
                return new Promise((resolve, reject) => {
                    reject('Invalid file type');
                });
            }
            const kmlText = await zipFile.files[kmlFile].async('text');
            let domObj = (new DOMParser()).parseFromString(kmlText, 'text/xml')
            let gjson = togeojson.kml(domObj);
            return gjson;
        }

        const response = await fetch(url);
        const kmlText = await response.text();
        if (url.toLowerCase().endsWith('.kml')) {
            let domObj = (new DOMParser()).parseFromString(kmlText, 'text/xml')
            let gjson = togeojson.kml(domObj);
            return gjson;
        } else if (url.toLowerCase().endsWith('.geojson')) {
            return new Promise((resolve, reject) => {
                resolve(JSON.parse(kmlText));
            });
        }
        return new Promise((resolve, reject) => {
            reject('Invalid file type');
        });
    }

    setPolylines = (gjsonPromise) => {
        const polylines: any[] = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'LineString') {
                    const line: any[] = [];
                    f.geometry.coordinates.forEach(c => {
                        line.push({lng: c[0], lat: c[1]});
                    });
                    polylines.push(line);
                } else if (f.geometry.type == 'MultiPolygon') {
                    const paths: any[] = [];
                    f.geometry.coordinates.forEach(path => {
                        const pol: any[] = [];
                        path[0].forEach(c => {
                            pol.push({lng: c[0], lat: c[1]});
                        });
                        paths.push(pol);
                    });
                    this.setState({...this.state, multi_polygons: paths});
                } else if (f.geometry.type == 'MultiLineString') {
                    f.geometry.coordinates.forEach(path => {
                        const line: any[] = [];
                        path.forEach(c => {
                            line.push({lng: c[0], lat: c[1]});
                        });
                        polylines.push(line);
                    });
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'LineString') {
                            const line: any[] = [];
                            g.coordinates.forEach(c => {
                                line.push({lng: c[0], lat: c[1]});
                            });
                            polylines.push(line);
                        }
                    });
                }
            });
            this.setState({...this.state, polylines: polylines});
            this.setPolygons(gjsonPromise);

        })
    };

    setPolygons = (gjsonPromise) => {
        const polygons: any = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'Polygon') {
                    const pol: any = [];
                    f.geometry.coordinates[0].forEach(c => {
                        pol.push({lng: c[0], lat: c[1]});
                    });
                    polygons.push(pol);
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'Polygon') {
                            const pol: any = [];
                            g.coordinates[0].forEach(c => {
                                pol.push({lng: c[0], lat: c[1]});
                            });
                            polygons.push(pol);
                        }
                    });
                }
            });
            this.setState({...this.state, polygons: polygons});
            this.setMarkers(gjsonPromise);
        })
    }

    setMarkers = (gjsonPromise) => {
        const markers: any[] = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'Point') {
                    const coor = f.geometry.coordinates;
                    markers.push({lng: coor[0], lat: coor[1]});
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'Point') {
                            const coor = g.coordinates;
                            markers.push({lng: coor[0], lat: coor[1]});
                        }
                    })
                }
            });
            if (markers.length == 0 && this.state.polylines && this.state.polylines[0] && this.props.selectable) {
                let firstLine = this.state.polylines[0][0];
                markers.push(firstLine);
            } else if (markers.length == 0 && this.state.polygons && this.state.polygons[0] && this.props.selectable) {
                let firstPolygon = this.state.polygons[0][0];
                markers.push(firstPolygon);
            }
            this.setState({...this.state, markers: markers});

        });
    }

    renderPolylines = (lines: any[]) => {
        const {visible} = this.props;
        const options = this.active ? this.getActiveSettings() : this.getInactiveSettings();
        return (
            <Polyline
                onClick={() => this.onClick()}
                path={lines}
                visible={visible}
                options={options}
            />
        );
    };

    renderPolygons = (polygon) => {
        const {visible} = this.props;
        const options = this.active ? this.getActiveSettings() : this.getInactiveSettings();
        return (
            <Polygon
                onClick={() => this.onClick()}
                path={polygon}
                visible={visible}
                options={options}
            />
        )
    }

    renderMarkers = (marker: any) => {
        const first = this.state.markers.indexOf(marker) == 0;
        const {visible, selectable} = this.props;
        const {selected} = this.state;
        return (
            <Marker
                onClick={() => this.onClick()}
                position={marker}
                icon={this.icon}
                visible={visible}
                {...this.markerProps}
            >
                {selected && first && selectable && (
                    <ProjectPopup
                        id={this.props.project.code}
                        image={this.props.project.image}
                        title={this.props.project.name}
                        stageOrder={this.props.project.iirsaStage.order}
                        onClose={() => this.onClick()}
                        infrastructure={this.props.project.iirsaSector}
                        subsector={this.props.project.iirsaSubsector}
                        countries={this.props.project.countries}/>
                )}
            </Marker>
        )
    }

    onClick() {
        this.setState({...this.state, selected: !this.state.selected});
    }

    render() {
        const {
            Size,
            Point,
            Marker: {MAX_ZINDEX}
        } = window['google'].maps; // eslint-disable-line dot-notation

        this.active = this.state.selected || this.props.hovered;

        // this.imageName = TRANSPORT_SUBSECTORS_LIST.find(word => word === this.props.project.subsector) || this.props.project.infrastructure;
        const sectorName = this.props.project?.iirsaSector?.name?.toLowerCase() || '';
        const subsectorName = this.props.project?.iirsaSubsector?.name?.toLowerCase();
        this.imageName = ((sectorName === 'transporte' && subsectorName) ? subsectorName : sectorName) || '';

        this.icon = {
            url: getMarkerImage(this.imageName, this.active),
            scaledSize: new Size(MARKER_HEIGHT, MARKER_WIDTH),
            anchor: new Point(MARKER_WIDTH / 2 + MARKER_OFFSET_HORIZONTAL, MARKER_HEIGHT + MARKER_OFFSET_VERTICAL)
        };
        this.markerProps = this.active ? {zIndex: MAX_ZINDEX + 1} : {};

        return (
            <>
                {this.state.polylines.length > 0 && this.state.polylines.map(this.renderPolylines)}
                {this.state.polygons.length > 0 && this.state.polygons.map(this.renderPolygons)}
                {this.state.markers.length > 0 && this.state.markers.map(this.renderMarkers)}
                {this.state.multi_polygons.length > 0 && this.state.multi_polygons.map(this.renderPolygons)}
            </>
        );
    }

}

// const mapDispatchToProps = (dispatch, ownProps: Props) => ({
//   onSetSelected: () => dispatch(projectsActions.setSelectedProjectId(ownProps.project.id)),
//   onUnsetSelected: () => dispatch(projectsActions.setSelectedProjectId(null))
// });

export default ProjectMarker;

